import React, { useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import useGetUser from "helpers/useGetUser";
import { isAdmin } from "helpers";
import PropertyActivity from "./PropertyActivity";
import PropertyAccessControlGroups from "./PropertyAccessControlGroups";
import PropertyGuests from "./PropertyGuests";
import PropertyUnits from "./PropertyUnits";
import PropertyMigrateModal from "./PropertyMigrateModal";
import PropertyEditModal from "./PropertyEditModal";
import PropertyStatusModal from "./PropertyStatusModal";
import PropertyReonboardModal from "./PropertyReonboardModal";
import PropertySiteCards from "./PropertySiteCards";
import PropertySyncRecords from "./PropertySyncRecords";
import {
  type PropertyConfigResponse,
  usePropertyConfig,
} from "./hooks/usePropertyData";
import { ArrowLeft, ArrowRightLeft, Pencil, RefreshCw } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { statusBadgeVariant } from "shared/utils/statusChipColor";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

const navLinks = [
  { to: "activity", label: "Activity" },
  { to: "groups", label: "Groups" },
  { to: "units", label: "Units" },
  { to: "guests", label: "Guests" },
  { to: "sync-records", label: "Sync Records" },
];

function PropertyNavigation({ externalId }: { externalId: string }) {
  const basePath = `/admin/properties/${externalId}`;
  const location = useLocation();
  const navigate = useNavigate();
  const activeTo =
    navLinks.find(({ to }) => {
      const path = `${basePath}/${to}`;
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    })?.to ?? navLinks[0].to;

  return (
    <Tabs value={activeTo} onValueChange={(value) => navigate(`${basePath}/${value}`)}>
      <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
        {navLinks.map(({ to, label }) => (
          <TabsTrigger
            key={to}
            value={to}
            className="shrink-0 data-[state=active]:bg-slate-200!"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function PropertyDetailItem({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-4 px-4 py-3">
      <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
        {label}
      </dt>
      <dd
        className={`min-w-0 text-sm font-medium wrap-break-word text-slate-800 ${mono ? "font-mono" : ""}`}
      >
        {children}
      </dd>
    </div>
  );
}

function PropertyDetails({
  config,
  externalId,
  isLoading,
  onEdit,
  onMigrate,
  onReonboard,
  onStatusChange,
}: {
  config?: PropertyConfigResponse;
  externalId: string;
  isLoading: boolean;
  onEdit: () => void;
  onMigrate: () => void;
  onReonboard: () => void;
  onStatusChange: () => void;
}) {
  return (
    <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
      <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Property details</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Configuration and connectivity
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Edit property"
                className="text-primary hover:text-primary"
                onClick={onEdit}
                size="icon-sm"
                type="button"
                variant="ghost"
              >
                <Pencil />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit property</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Re-onboard property"
                className="text-primary hover:text-primary"
                onClick={onReonboard}
                size="icon-sm"
                type="button"
                variant="ghost"
              >
                <RefreshCw />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Re-onboard property</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Migrate property"
                className="text-primary hover:text-primary"
                disabled={isLoading}
                onClick={onMigrate}
                size="icon-sm"
                type="button"
                variant="ghost"
              >
                <ArrowRightLeft />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Migrate property</TooltipContent>
          </Tooltip>
        </div>
      </header>
      <dl className="divide-y divide-slate-100">
        <PropertyDetailItem label="External ID" mono>
          {externalId}
        </PropertyDetailItem>
        <PropertyDetailItem label="Status">
          {isLoading ? (
            <Spinner className="size-4" />
          ) : (
            <Badge asChild variant={statusBadgeVariant(config?.status)}>
              <button
                aria-label="Change property status"
                className="cursor-pointer"
                onClick={onStatusChange}
                type="button"
              >
                {config?.status ?? "Staging"}
              </button>
            </Badge>
          )}
        </PropertyDetailItem>
        <PropertyDetailItem label="Physical key extension">
          {isLoading ? (
            <Spinner className="size-4" />
          ) : (
            <Badge variant={config?.physicalKeyExtension ? "success" : "secondary"}>
              {config?.physicalKeyExtension ? "Enabled" : "Disabled"}
            </Badge>
          )}
        </PropertyDetailItem>
      </dl>
    </section>
  );
}

export default function PropertyPage() {
  const params = useParams<{ externalId: string }>();
  const { data: user } = useGetUser(true);
  const admin = isAdmin(user);

  const externalId = params.externalId!;
  const { data: config, isLoading: configLoading } = usePropertyConfig(externalId);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showReonboardModal, setShowReonboardModal] = useState(false);

  if (!admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
      <header className="pb-5 border-b border-slate-200">
        <Link
          to="/admin/properties"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Properties
        </Link>
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="px-1">
            <h1 className="text-2xl font-semibold text-slate-900">
              {configLoading ? "Property Details" : config?.name || "Property Details"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">{externalId}</p>
          </div>
          <PropertyDetails
            config={config}
            externalId={externalId}
            isLoading={configLoading}
            onEdit={() => setShowEditModal(true)}
            onMigrate={() => setShowMigrateModal(true)}
            onReonboard={() => setShowReonboardModal(true)}
            onStatusChange={() => setShowStatusModal(true)}
          />
        </aside>

        <section className="min-w-0">
          <PropertyNavigation externalId={externalId} />
          <div className="pt-6 space-y-6">
            <PropertySiteCards propertyExternalId={externalId} />
            <Routes>
              <Route path="activity" element={<PropertyActivity />} />
              <Route path="groups" element={<PropertyAccessControlGroups />} />
              <Route path="units" element={<PropertyUnits />} />
              <Route path="guests" element={<PropertyGuests />} />
              <Route path="sync-records" element={<PropertySyncRecords />} />
              <Route index element={<Navigate to="activity" replace />} />
            </Routes>
          </div>
        </section>
      </div>
      <PropertyMigrateModal
        show={showMigrateModal}
        onHide={() => setShowMigrateModal(false)}
        oldPropertyExternalId={externalId}
        currentOldPropertyStatus={config?.status}
      />
      <PropertyEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        propertyExternalId={externalId}
        currentConfig={config}
      />
      <PropertyStatusModal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        propertyExternalId={externalId}
        currentStatus={config?.status}
      />
      <PropertyReonboardModal
        show={showReonboardModal}
        onHide={() => setShowReonboardModal(false)}
        propertyExternalId={externalId}
      />
    </main>
  );
}
